import { FC, useEffect, useMemo, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { TrackedNotificationBox } from '@eneco/components/TrackedNotificationBox/TrackedNotificationBox';
import { Layout, NextButton, StepHeader } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Box, InputUsage, RadioButton, RadioGroup, Stack, Text, TextLink } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SmeUsageStepRendering } from '@sitecore/types/manual/smeFlowExperiment/SMEUsageStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { AddBusinessDetailsDialog } from './AddBusinessDetailsDialog';
import usageEstimatesSme from '../../actions/setUsageEstimatedValuesSme.data';
import { FlowContextSme } from '../../context';
import { energyTypeIncludes, getMaxUsageValue, multiplyByFloorArea } from '../../helpers';
import { useFlowTracking } from '../../hooks';
import { EnergyType } from '../../types';
interface FormFields {
  hasDoubleMeter?: string;
  usageElectricityHigh?: number;
  usageElectricityLow?: number;
  usageGas?: number;
  usageWarmth?: number;
  usageWater?: number;
}

export const UsageStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();
  const { send: sendToFlowMachine } = useFlowActorRef();
  const energyType = useFlowSelector(state => state.context.energyType) || EnergyType.Electricity;
  const hasDoubleMeter = useFlowSelector(state => state.context.hasDoubleMeter) || true;
  const usageElectricityHigh = useFlowSelector(state => state.context.usageElectricityHigh);
  const usageElectricityLow = useFlowSelector(state => state.context.usageElectricityLow);
  const usageGas = useFlowSelector(state => state.context.usageGas);
  const usageWarmth = useFlowSelector(state => state.context.usageWarmth);
  const usageWater = useFlowSelector(state => state.context.usageWater);

  const { SmeUsageStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SmeUsageStep: SmeUsageStepRendering;
  }>();

  const MAX_USAGE_VALUE_ELECTRICITY_HIGH = getMaxUsageValue('ELECTRICITY_HIGH');
  const MAX_USAGE_VALUE_ELECTRICITY_LOW = getMaxUsageValue('ELECTRICITY_LOW');
  const MAX_USAGE_VALUE_GAS = getMaxUsageValue('GAS');
  const MAX_USAGE_VALUE_WARMTH = getMaxUsageValue('WARMTH');
  const MAX_USAGE_VALUE_WATER = getMaxUsageValue('WATER');

  const [shouldShowDoubleMeter, setShouldShowDoubleMeter] = useState(!!hasDoubleMeter);
  const [shouldShowInputFields, setShouldShowInputFields] = useState(
    !energyTypeIncludes(energyType, EnergyType.Electricity) || hasDoubleMeter !== undefined,
  );
  const [shouldShowBlockingNotification, setShouldShowBlockingNotification] = useState(false);
  const [shouldShowOfferAvailabilityNotification, setShouldShowOfferAvailabilityNotification] = useState(false);

  const formSchema = yup.object({
    ...(energyTypeIncludes(energyType, EnergyType.Electricity) && {
      hasDoubleMeter: yup.string().required(textualData?.meterType?.meterTypeRadio?.value?.requiredMessage),

      ...(shouldShowInputFields && {
        usageElectricityHigh: yup
          .number()
          .typeError(textualData?.usage?.electricityHighFormField?.value?.validationMessage ?? '')
          .required(textualData?.usage?.electricityHighFormField?.value?.requiredMessage)
          .min(1, textualData?.usage?.electricityHighFormField?.value?.validationMessage)
          .max(
            MAX_USAGE_VALUE_ELECTRICITY_HIGH,
            textualData?.usage?.electricityHighFormField?.value?.validationMessage,
          ),

        ...(shouldShowDoubleMeter && {
          usageElectricityLow: yup
            .number()
            .typeError(textualData?.usage?.electricityLowFormField?.value?.validationMessage ?? '')
            .required(textualData?.usage?.electricityLowFormField?.value?.requiredMessage)
            .min(1, textualData?.usage?.electricityLowFormField?.value?.validationMessage)
            .max(
              MAX_USAGE_VALUE_ELECTRICITY_LOW,
              textualData?.usage?.electricityLowFormField?.value?.validationMessage,
            ),
        }),
      }),
    }),

    ...(shouldShowInputFields && {
      ...(energyTypeIncludes(energyType, EnergyType.Gas) && {
        usageGas: yup
          .number()
          .typeError(textualData?.usage?.gasFormField?.value?.validationMessage ?? '')
          .required(textualData?.usage?.gasFormField?.value?.requiredMessage)
          .min(1, textualData?.usage?.gasFormField?.value?.validationMessage)
          .max(MAX_USAGE_VALUE_GAS, textualData?.usage?.gasFormField?.value?.validationMessage),
      }),

      ...(energyTypeIncludes(energyType, EnergyType.Warmth) && {
        usageWarmth: yup
          .number()
          .integer(textualData?.usage?.warmthFormField?.value?.validationMessage)
          .typeError(textualData?.usage?.warmthFormField?.value?.validationMessage ?? '')
          .required(textualData?.usage?.warmthFormField?.value?.requiredMessage)
          .min(1, textualData?.usage?.warmthFormField?.value?.validationMessage)
          .max(MAX_USAGE_VALUE_WARMTH, textualData?.usage?.warmthFormField?.value?.validationMessage),
      }),

      ...(energyTypeIncludes(energyType, EnergyType.TapWater) && {
        usageWater: yup
          .number()
          .integer(textualData?.usage?.warmWaterFormField?.value?.validationMessage)
          .typeError(textualData?.usage?.warmWaterFormField?.value?.validationMessage ?? '')
          .required(textualData?.usage?.warmWaterFormField?.value?.requiredMessage)
          .min(1, textualData?.usage?.warmWaterFormField?.value?.validationMessage)
          .max(MAX_USAGE_VALUE_WATER, textualData?.usage?.warmWaterFormField?.value?.validationMessage),
      }),
    }),
  });

  const {
    register,
    unregister,
    handleSubmit,
    control,
    getValues,
    resetField,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: {
      ...(energyTypeIncludes(energyType, EnergyType.Electricity) && {
        hasDoubleMeter: hasDoubleMeter ? 'double' : 'single',
        usageElectricityHigh,
        usageElectricityLow,
      }),

      ...(energyTypeIncludes(energyType, EnergyType.Gas) && {
        usageGas,
      }),

      ...(energyTypeIncludes(energyType, EnergyType.Warmth) && {
        usageWarmth,
      }),

      ...(energyTypeIncludes(energyType, EnergyType.TapWater) && {
        usageWater,
      }),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  const { trackFillUsage } = useFlowTracking();

  const watchHasDoubleMeter = watch('hasDoubleMeter');
  const watchUsageElectricityHigh = watch('usageElectricityHigh');
  const watchUsageElectricityLow = watch('usageElectricityLow');
  const watchUsageGas = watch('usageGas');

  useEffect(() => {
    if (energyTypeIncludes(energyType, EnergyType.Electricity)) {
      resetField('usageElectricityHigh');
      resetField('usageElectricityLow');

      unregister(['usageElectricityHigh', 'usageElectricityLow']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowDoubleMeter]);

  useEffect(() => {
    if (shouldShowInputFields) {
      resetField('usageElectricityHigh');
      resetField('usageElectricityLow');
      resetField('usageGas');
      resetField('usageWarmth');
      resetField('usageWater');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShowInputFields]);

  useEffect(() => {
    if (isSubmitSuccessful) {
      // Mutated form field values are bound to a "string" type
      const formData = getValues();

      const values = {
        hasDoubleMeter: formData.hasDoubleMeter ? formData.hasDoubleMeter === 'double' : undefined,

        // truthy check to filter out 0 'zero' values
        usageElectricityHigh: Number(formData.usageElectricityHigh) || undefined,
        usageElectricityLow: Number(formData.usageElectricityLow) || undefined,
        usageGas: Number(formData.usageGas) || undefined,
        usageWarmth: Number(formData.usageWarmth) || undefined,
        usageWater: Number(formData.usageWater) || undefined,
      };

      trackFillUsage({ ...values, isUsageKnown: true });

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    setShouldShowBlockingNotification(
      (watchUsageElectricityHigh ?? 0) > MAX_USAGE_VALUE_ELECTRICITY_HIGH ||
        (watchUsageElectricityLow ?? 0) > MAX_USAGE_VALUE_ELECTRICITY_LOW ||
        (watchUsageGas ?? 0) > MAX_USAGE_VALUE_GAS,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchUsageElectricityLow, watchUsageElectricityHigh, watchUsageGas]);

  useEffect(() => {
    setShouldShowDoubleMeter(watchHasDoubleMeter === 'double');
    setShouldShowInputFields(!energyTypeIncludes(energyType, EnergyType.Electricity) || !!watchHasDoubleMeter);

    setShouldShowOfferAvailabilityNotification(
      watchHasDoubleMeter === 'single' &&
        !!textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content,
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchHasDoubleMeter]);

  const addBusinessDetailsFormSubmit = ({
    industryChoice,
    floorArea = 1,
  }: {
    industryChoice: string;
    floorArea: number;
  }) => {
    const estimatedValues = usageEstimatesSme.find(estimate => estimate.industry === industryChoice);

    if (estimatedValues) {
      const { usageElectricityHigh = 0, usageElectricityLow = 0, usageGas = 0 } = estimatedValues;
      const usageElectricityHighValue = multiplyByFloorArea(usageElectricityHigh, floorArea);
      const usageElectricityLowValue = multiplyByFloorArea(usageElectricityLow, floorArea);

      if (energyTypeIncludes(energyType, EnergyType.Electricity)) {
        if (watchHasDoubleMeter === 'double') {
          setValue('usageElectricityHigh', usageElectricityHighValue);
          setValue('usageElectricityLow', usageElectricityLowValue);
        } else {
          setValue('usageElectricityHigh', usageElectricityHighValue + usageElectricityLowValue);
        }
      }

      if (energyTypeIncludes(energyType, EnergyType.Gas)) {
        setValue('usageGas', multiplyByFloorArea(usageGas, floorArea));
      }
      trigger();
    }
  };

  const hasElectricityType = useMemo(() => energyTypeIncludes(energyType, EnergyType.Electricity), [energyType]);

  const usageFieldsOutput =
    hasElectricityType || shouldShowInputFields ? (
      <Stack.Item>
        <Box paddingBottom="4">
          <Stack direction="column" gap="6">
            {hasElectricityType && (
              <>
                <Stack.Item>
                  <Controller
                    control={control}
                    name="hasDoubleMeter"
                    render={({ field: { onChange, value, name, ref } }) => (
                      <RadioGroup
                        ref={ref}
                        label={textualData?.meterType?.meterTypeRadio?.value?.label ?? ''}
                        name={name}
                        direction="row"
                        value={value}
                        onValueChange={onChange}
                        error={errors.hasDoubleMeter?.message}>
                        {textualData?.meterType?.meterTypeRadio.value?.options?.map(option => (
                          <RadioButton value={option.name} key={option.name}>
                            {option.label}
                          </RadioButton>
                        ))}
                      </RadioGroup>
                    )}
                  />

                  <Box paddingTop="2">
                    <Text size="BodyS" color="textLowEmphasis">
                      <TrackedDialog
                        title={textualData?.meterType?.meterTypeDialog?.value?.title}
                        trigger={<TextLink>{textualData?.meterType?.meterTypeDialog?.value?.triggerText}</TextLink>}>
                        <Box paddingBottom="6">
                          <RichText html={textualData?.meterType?.meterTypeDialog?.value?.content} />
                        </Box>
                      </TrackedDialog>
                    </Text>
                  </Box>
                </Stack.Item>

                {shouldShowInputFields && (
                  <>
                    <InputUsage
                      {...register('usageElectricityHigh')}
                      error={errors.usageElectricityHigh?.message}
                      label={textualData?.usage?.electricityHighFormField?.value?.label ?? ''}
                      placeholder={textualData?.usage?.electricityHighFormField?.value?.placeholder}
                      hint={textualData?.usage?.electricityHighFormField?.value?.hint}
                      type="energy"
                      unit="kWh"
                    />

                    {shouldShowDoubleMeter && (
                      <InputUsage
                        {...register('usageElectricityLow')}
                        error={errors.usageElectricityLow?.message}
                        label={textualData?.usage?.electricityLowFormField?.value?.label ?? ''}
                        placeholder={textualData?.usage?.electricityLowFormField?.value?.placeholder}
                        hint={textualData?.usage?.electricityLowFormField?.value?.hint}
                        type="energy"
                        unit="kWh"
                      />
                    )}
                  </>
                )}
              </>
            )}
            {shouldShowInputFields && (
              <>
                {energyTypeIncludes(energyType, EnergyType.Gas) && (
                  <InputUsage
                    {...register('usageGas')}
                    error={errors.usageGas?.message}
                    label={textualData?.usage?.gasFormField?.value?.label ?? ''}
                    placeholder={textualData?.usage?.gasFormField?.value?.placeholder}
                    hint={textualData?.usage?.gasFormField?.value?.hint}
                    type="gas"
                    unit="m3"
                  />
                )}

                {energyTypeIncludes(energyType, EnergyType.Warmth) && (
                  <InputUsage
                    {...register('usageWarmth')}
                    error={errors.usageWarmth?.message}
                    label={textualData?.usage?.warmthFormField?.value?.label ?? ''}
                    placeholder={textualData?.usage?.warmthFormField?.value?.placeholder}
                    hint={textualData?.usage?.warmthFormField?.value?.hint}
                    type="warmth"
                    unit="GJ"
                  />
                )}

                {energyTypeIncludes(energyType, EnergyType.TapWater) && (
                  <InputUsage
                    {...register('usageWater')}
                    error={errors.usageWater?.message}
                    label={textualData?.usage?.warmWaterFormField?.value?.label ?? ''}
                    placeholder={textualData?.usage?.warmWaterFormField?.value?.placeholder}
                    hint={textualData?.usage?.warmWaterFormField?.value?.hint}
                    type="water"
                    unit="m3"
                  />
                )}
              </>
            )}

            {shouldShowOfferAvailabilityNotification && (
              <TrackedNotificationBox
                isAlert={false}
                label={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title}
                title={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.title ?? ''}
                text={
                  <RichText html={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.content} />
                }
                variant={textualData?.notifications?.offerAvailabilityWarningNotification?.value?.variant}
              />
            )}

            {shouldShowBlockingNotification && (
              <TrackedNotificationBox
                label={textualData?.notifications?.errorUsagesExceededNotification?.value?.title}
                title={textualData?.notifications?.errorUsagesExceededNotification?.value?.title ?? ''}
                isAlert
                text={<RichText html={textualData?.notifications?.errorUsagesExceededNotification?.value?.content} />}
                variant={textualData?.notifications?.errorUsagesExceededNotification?.value?.variant}
              />
            )}
          </Stack>
        </Box>
      </Stack.Item>
    ) : null;

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <StepHeader textualData={textualData} />
      {energyTypeIncludes(energyType, EnergyType.Electricity) || energyTypeIncludes(energyType, EnergyType.Gas) ? (
        <AddBusinessDetailsDialog handleSubmit={addBusinessDetailsFormSubmit} />
      ) : null}

      {usageFieldsOutput}

      {!shouldShowBlockingNotification && (
        <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
      )}
    </Layout.Content>
  );
};
